// React / Redux / Related
import React, { Fragment, useEffect, useState } from 'react';
import { Button } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { Translate } from 'react-redux-i18n';
import { I18n } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

// Helpers
import RecordResultMessage from '../../../components/common/recordResult';
import { Record } from '../../../modules/bin/utility';
import get from 'lodash.get';

// Components
import EventCloneTemplate from './template';
import BasePage from '../../../components/common/basePageView';
import Spinner from '../../../components/common/spinner';

// Constants
import config from '../../../constants/index';
import SavedEntityModal from '../../../components/common/savedEntityModal';
import Text from '../../../components/common/text';
import { cloneRecord, getRecord, clearRecordCloneMessages } from '../store/eventActions';
import { RootState } from '../../../store';
import { RouteParams } from '../../../types/routeParams';
import { useAppDispatch } from '../../../hooks';

const getSavedModalContent = (name: string) => {
  return (
    <Fragment>
      <Text>
        {I18n.t(`event.template.page.saved-modal.event-create-message`).replace(
          '{eventName}',
          name
        )}
      </Text>
    </Fragment>
  );
};

const getSavedModalButtons = (url: string, navigate: (path: string) => void) => {
  return (
    <Fragment>
      <Button
        className="primary"
        onClick={() => {
          window.open(url, '_blank');
        }}
      >
        {I18n.t('event.template.page.saved-modal.event-view-button')}
      </Button>
      <Button onClick={() => navigate(config.DEFAULT_EVENTS_URL)}>
        {I18n.t('event.template.page.saved-modal.event-return-button')}
      </Button>
    </Fragment>
  );
};

const CloneEventContainer = () => {
  const eventSelector = useSelector((state: RootState) => state.event);
  const dispatch = useAppDispatch();
  const { id } = useParams<RouteParams>();
  const navigate = useNavigate();
  const [isSavedModalOpened, setIsSavedModalOpened] = useState(false);
  // prettier-ignore
  const [isSavedOperationFinished, setIsSavedOperationFinished] = useState(false);
  const { record, recordClone } = eventSelector;
  const isSaving = Record.isRecordLoading(recordClone);

  useEffect(() => {
    dispatch(getRecord(id));
  }, [id]);

  const onSave = (data: any) => {
    dispatch(clearRecordCloneMessages());
    dispatch(cloneRecord(id, data));
    setIsSavedOperationFinished(true);
  };

  const isRecordReady = Record.isRecordReady(record);
  if (!isRecordReady) {
    return <Spinner />;
  }

  const goToEventView = () => {
    if (isSavedOperationFinished) {
      setIsSavedModalOpened(true);
      setIsSavedOperationFinished(false);
    }
  };

  const successModalRedirectButtons = getSavedModalButtons(
    get(recordClone, 'data.urlFull'),
    navigate
  );
  const successModalContent = getSavedModalContent(
    get(recordClone, 'data.name')
  );

  return (
    <BasePage>
      <RecordResultMessage
        record={recordClone}
        onDismiss={() => dispatch(clearRecordCloneMessages())}
        redirectOnSuccess={goToEventView}
      />

      <SavedEntityModal
        open={isSavedModalOpened}
        content={successModalContent}
        buttons={successModalRedirectButtons}
        isEdit={false}
      />

      <h1>
        <Translate value="event.template.page.setup-clone.title" />
      </h1>
      <EventCloneTemplate onSave={onSave} isLoading={isSaving} />
    </BasePage>
  );
};

export default CloneEventContainer;
